/**
 * Alerts the user when a Research Assistant run finishes while they are not
 * looking at it (tab inactive, or a different session on screen).
 *
 * Gated by the ``ra_run_completion_notification`` flag. Callers feed it the
 * live run lifecycle; replayed (historical) events must not be passed in.
 */

import { useCallback, useMemo, useRef, type MutableRefObject } from 'react';

import { useFeatureFlagSplit } from '@verantos-dev/app-state-base';

import { epiChatMessagesEn } from '../locale/en';
import {
  badgeTabTitle,
  isTabInactive,
  playChime,
  primeChime,
  requestNotificationPermission,
  shouldAlertRunCompletion,
  showNativeNotification,
} from '../utils/runCompletionAlert';

export const RUN_COMPLETION_NOTIFICATION_FLAG = 'ra_run_completion_notification';

export interface RunCompletionNotifier {
  noteRunStarted: (sessionId: string) => void;
  noteRunCancelled: (sessionId: string) => void;
  noteRunFinished: (sessionId: string) => void;
  /** Call from a user gesture (e.g. sending a message). */
  requestPermission: () => void;
}

// TODO: cleanup with ra_run_completion_notification ff
export const useRunCompletionNotifier = (
  activeSessionIdRef: MutableRefObject<string | null>
): RunCompletionNotifier => {
  const enabled = Boolean(useFeatureFlagSplit(RUN_COMPLETION_NOTIFICATION_FLAG));
  const enabledRef = useRef(enabled);
  enabledRef.current = enabled;
  const startedAtRef = useRef<Map<string, number>>(new Map());
  const cancelledRef = useRef<Set<string>>(new Set());

  const noteRunStarted = useCallback((sessionId: string) => {
    startedAtRef.current.set(sessionId, Date.now());
    cancelledRef.current.delete(sessionId);
  }, []);

  const noteRunCancelled = useCallback((sessionId: string) => {
    cancelledRef.current.add(sessionId);
  }, []);

  const noteRunFinished = useCallback(
    (sessionId: string) => {
      const startedAt = startedAtRef.current.get(sessionId);
      const cancelled = cancelledRef.current.has(sessionId);
      startedAtRef.current.delete(sessionId);
      cancelledRef.current.delete(sessionId);
      if (!enabledRef.current) return;

      const tabInactive = isTabInactive();
      const shouldAlert = shouldAlertRunCompletion({
        durationMs: startedAt === undefined ? null : Date.now() - startedAt,
        tabInactive,
        isActiveSession: activeSessionIdRef.current === sessionId,
        cancelled,
      });
      if (!shouldAlert) return;

      if (tabInactive) badgeTabTitle();
      playChime();
      showNativeNotification(
        epiChatMessagesEn['epiChat.runComplete.title'],
        epiChatMessagesEn['epiChat.runComplete.body'],
        `ra-run-complete-${sessionId}`
      );
    },
    [activeSessionIdRef]
  );

  const requestPermission = useCallback(() => {
    if (!enabledRef.current) return;
    requestNotificationPermission();
    primeChime();
  }, []);

  return useMemo(
    () => ({ noteRunStarted, noteRunCancelled, noteRunFinished, requestPermission }),
    [noteRunStarted, noteRunCancelled, noteRunFinished, requestPermission]
  );
};
